import _ from 'lodash';
import * as actionTypes from './dynamicPagesActionTypes';
import * as selectors from './dynamicPagesSelectors';
import * as util from '#packages/util';
import { EditorPlatformHostIntegrationAPI } from '@wix/editor-platform-host-integration-apis';
import type {
  DSAction,
  DSRead,
  SitemapEntry,
  CurrentRouteInfo,
  RoutersDefinition,
} from 'types/documentServices';
import constants from '#packages/constants';
import type { Dispatch } from 'redux';
import type { EditorState } from '@/stateManagement';
import type { EditorAPI } from '@/editorAPI';
import type { PageRef } from '@wix/document-services-types';

const setInnerRoutes = (innerRoutes: SitemapEntry[]) => ({
  type: actionTypes.SET_INNER_ROUTES,
  innerRoutes,
});
const setPreventFetch = (shouldPrevent: boolean) => ({
  type: actionTypes.SET_PREVENT_FETCH,
  shouldPrevent,
});
const resetInnerRoutes = () => ({ type: actionTypes.RESET_INNER_ROUTES });
const setRouterData = (routerData: RoutersDefinition) => ({
  type: actionTypes.SET_ROUTER_DATA,
  routerData,
});
const resetRouterData = () => ({ type: actionTypes.RESET_ROUTER_DATA });
const setSelectedInnerRoute = (selectedInnerRoute: string) => ({
  type: actionTypes.SET_SELECTED_INNER_ROUTE,
  selectedInnerRoute,
});
const setCurrentRouteInfo = (currentRouteInfo: CurrentRouteInfo) => ({
  type: actionTypes.SET_CURRENT_ROUTE_INFO,
  currentRouteInfo,
});
const resetSelectedInnerRoute = () => ({
  type: actionTypes.RESET_SELECTED_INNER_ROUTE,
});
const setInnerRouteFetchProcess = () => ({
  type: actionTypes.SET_INNER_ROUTES_FETCH_PROCESS,
});
const resetInnerRouteFetchProcess = () => ({
  type: actionTypes.RESET_INNER_ROUTES_FETCH_PROCESS,
});
const setInnerRoutesCounts = ({
  innerRoutesCounts,
}: {
  innerRoutesCounts: Record<string, number>;
}) => ({
  type: actionTypes.SET_INNER_ROUTES_COUNT,
  innerRoutesCounts,
});
const clearInnerRoutesCounts = () => ({
  type: actionTypes.CLEAR_INNER_ROUTES_COUNT,
});
const setCachedInnerRoutes = (
  routerData: RoutersDefinition,
  pageId: string,
  innerRoutes: SitemapEntry[],
) => ({
  type: actionTypes.SET_CACHED_INNER_ROUTES,
  routerData,
  pageId,
  innerRoutes,
});

const getNavigationData = (
  newRoute: string,
  routerId: string,
  dsRead: DSRead,
) => {
  const linkItem = dsRead.data.createItem('DynamicPageLink');
  return Object.assign(linkItem, { innerRoute: newRoute || '', routerId });
};

const findInnerRouteIndex = (
  innerRoute: string,
  innerRoutes: SitemapEntry[],
) => {
  let index = innerRoutes.findIndex((route) =>
    isEqualIgnoreCase(innerRoute, route.url),
  );
  if (index === -1) {
    const decoded = decodeURIComponent(innerRoute);
    index = innerRoutes.findIndex((route) =>
      isEqualIgnoreCase(decoded, route.url),
    );
  }
  return index === -1 ? null : index;
};

const isEqualIgnoreCase = (str1: string, str2: string) =>
  str1.localeCompare(str2, undefined, { sensitivity: 'accent' }) === 0;

const navigateToRoute =
  (newRoute: string, routerId: string, dynamicPageIdOverride?: string) =>
  (
    dispatch: Dispatch,
    getState: () => EditorState,
    { dsRead, dsActions }: { dsRead: DSRead; dsActions: DSAction },
  ) => {
    const navData = getNavigationData(newRoute, routerId, dsRead);

    const currentInnerRoute = dsRead.routers.getCurrentInnerRoute();
    const isAlreadyOnThatRoute =
      currentInnerRoute?.routerId === navData.routerId &&
      (navData.innerRoute === currentInnerRoute.innerRoute ||
        (navData.innerRoute === '/' && currentInnerRoute.innerRoute === ''));

    if (!isAlreadyOnThatRoute) {
      dsActions.pages.navigateTo({ ...navData, dynamicPageIdOverride });
    }
  };

const resetAllDynamicPageData = () => (dispatch: Dispatch) => {
  dispatch(resetInnerRoutes());
  dispatch(resetRouterData());
  dispatch(resetSelectedInnerRoute());
};

const isCollectionPage = (innerRouters: SitemapEntry[]) =>
  innerRouters.length === 1 && innerRouters[0].url === '/';

const routerDataChanged = (getState: () => EditorState, dsRead: DSRead) => {
  const pageId = dsRead.pages.getPrimaryPageId();
  const oldRouterData = selectors.getRouterData(getState());
  const newRouterData = dsRead.routers.getRouterDataForPageIfExist(pageId);

  return !_.isEqual(oldRouterData, newRouterData);
};

const updateDynamicPagesState =
  () =>
  (
    dispatch: FunctionFixMe,
    getState: () => EditorState,
    { editorAPI, dsRead }: { editorAPI: EditorAPI; dsRead: DSRead },
  ) => {
    const oldCurrentRouteInfo = selectors.getCurrentRouteInfo(getState());
    const oldInnerRoutes = selectors.getInnerRoutes(getState());

    const currentRouteInfo = dsRead.routers.getCurrentInnerRoute();
    if (!_.isEqual(oldCurrentRouteInfo, currentRouteInfo)) {
      dispatch(setCurrentRouteInfo(currentRouteInfo));
    }

    if (!currentRouteInfo.isDynamic) {
      dispatch(resetAllDynamicPageData());
      return;
    }

    const oldRouterId = selectors.getRouterId(getState());
    const alreadyInFetchProcess =
      selectors.isInInnerRoutesFetchProcess(getState());
    const routerHasChanged = currentRouteInfo.routerId !== oldRouterId;
    const innerRouteHasChanged =
      oldCurrentRouteInfo?.innerRoute &&
      currentRouteInfo.innerRoute !== oldCurrentRouteInfo.innerRoute;
    if (!routerHasChanged && innerRouteHasChanged) {
      editorAPI.scroll.registerNewScrollPosition(editorAPI.scroll.get());
    }
    const isFetchEnabled = !selectors.shouldPreventFetch(getState());
    const wasOnCollectionPage = oldInnerRoutes
      ? isCollectionPage(oldInnerRoutes)
      : false;
    const navigatedFromCollectionToItemPage =
      !routerHasChanged &&
      wasOnCollectionPage &&
      currentRouteInfo.innerRoute !== '';
    if (
      isFetchEnabled &&
      ((!alreadyInFetchProcess && routerHasChanged) ||
        currentRouteInfo.innerRoute === undefined ||
        navigatedFromCollectionToItemPage ||
        routerDataChanged(getState, dsRead))
    ) {
      return dispatch(fetchInnerRoutes());
    }

    const oldSelectedInnerRoute = selectors.getSelectedInnerRoute(getState());
    if (currentRouteInfo.innerRoute !== oldSelectedInnerRoute) {
      dispatch(setSelectedInnerRoute(currentRouteInfo.innerRoute));
    }
  };

const setSelectedInnerRouteAndNavigate =
  (newInnerRoute: string, routerId: string, dynamicPageIdOverride?: string) =>
  (dispatch: FunctionFixMe, getState: () => EditorState) => {
    routerId = routerId ? routerId : selectors.getRouterId(getState());
    dispatch(navigateToRoute(newInnerRoute, routerId, dynamicPageIdOverride));
    dispatch(setSelectedInnerRoute(newInnerRoute));
  };

const preFetchAndCacheInnerRoutes =
  (routersData?: Record<string, RoutersDefinition>) =>
  (
    dispatch: FunctionFixMe,
    getState: () => EditorState,
    { editorAPI, dsRead }: { editorAPI: EditorAPI; dsRead: DSRead },
  ) => {
    routersData =
      routersData ||
      (editorAPI.routers.get.all() as Record<string, RoutersDefinition>);
    const requests: Promise<void>[] = [];
    util.fedopsLogger.interactionStarted(
      util.fedopsLogger.INTERACTIONS.PRE_FETCH_ROUTERS_INNER_ROUTES,
    );
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    Object.entries(routersData || {}).forEach(([routerId, routerData]) => {
      Object.values(routerData.pages || {})
        .filter((pageId) => {
          const pageRef = editorAPI.pages.getReference(pageId as string);
          return !platformAPI.pageReplace.isReplacer(pageRef as PageRef);
        })
        .forEach((pageId) => {
          requests.push(
            new Promise((resolve) => {
              dsRead.routers.getRouterInnerRoutes(
                routerId,
                pageId,
                (innerRoutes: SitemapEntry[]) => {
                  if (innerRoutes) {
                    dispatch(
                      setCachedInnerRoutes(
                        { ...routerData, routerId },
                        pageId,
                        innerRoutes,
                      ),
                    );
                  } else {
                    // fetching failed, ignore it at prefetch phase and handle it on page routes request
                  }
                  resolve();
                },
              );
            }),
          );
        });
    });

    return Promise.all(requests).then(() => {
      util.fedopsLogger.interactionEnded(
        util.fedopsLogger.INTERACTIONS.PRE_FETCH_ROUTERS_INNER_ROUTES,
      );
    });
  };

const getInnerRoutesFromCacheOrFetch = (
  {
    dsRead,
    dispatch,
    getState,
  }: { dispatch: FunctionFixMe; dsRead: DSRead; getState: () => EditorState },
  routerData: RoutersDefinition,
  pageId: string,
  forceFetch: boolean,
): Promise<SitemapEntry[]> => {
  const cachedRoutes = selectors.getCachedInnerRoutes(
    getState(),
    routerData.routerId,
    pageId,
  );
  if (cachedRoutes && !forceFetch) {
    return Promise.resolve(cachedRoutes);
  }

  dispatch(setInnerRouteFetchProcess());
  return new Promise((resolve) => {
    dsRead.routers.getRouterInnerRoutes(
      routerData.routerId,
      pageId,
      (fetchedRoutes: SitemapEntry[]) => {
        fetchedRoutes = fetchedRoutes || [];
        dispatch(resetInnerRouteFetchProcess());
        dispatch(setCachedInnerRoutes(routerData, pageId, fetchedRoutes));
        resolve(fetchedRoutes);
      },
    );
  });
};

const fetchInnerRoutes =
  (
    primaryPageId?: string,
    routerData?: RoutersDefinition,
    selectedRoute?: string,
    forceFetch?: boolean,
  ) =>
  (
    dispatch: FunctionFixMe,
    getState: () => EditorState,
    { dsRead, editorAPI }: { dsRead: DSRead; editorAPI: EditorAPI },
  ) => {
    let dynamicPageIdOverride: string;
    let initPrimaryPageId = primaryPageId || dsRead.pages.getPrimaryPageId();
    const initRouterData =
      routerData ||
      dsRead.routers.getRouterDataForPageIfExist(initPrimaryPageId);
    if (!initRouterData) {
      dispatch(resetAllDynamicPageData());
      return;
    }

    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    const pageRef = editorAPI.pages.getReference(initPrimaryPageId) as PageRef;
    const isVariantOrReplacerPage =
      !!platformAPI.pageReplace.getOriginalPageRef(pageRef);
    const { variants, replacers } =
      platformAPI.pageReplace.getPageVariations(pageRef);
    if (variants.length || replacers.length || isVariantOrReplacerPage) {
      dynamicPageIdOverride = initPrimaryPageId;
    }

    if (isVariantOrReplacerPage) {
      const routerConfig =
        typeof initRouterData.config === 'string'
          ? JSON.parse(initRouterData.config)
          : initRouterData.config || {};
      if (routerConfig.variantRoutesStrategy !== 'own') {
        const originalPageRef =
          platformAPI.pageReplace.getOriginalPageRef(pageRef);
        initPrimaryPageId = originalPageRef.id;
      }
    }

    return getInnerRoutesFromCacheOrFetch(
      { dsRead, dispatch, getState },
      initRouterData as RoutersDefinition,
      initPrimaryPageId,
      forceFetch,
    ).then((innerRoutes) => {
      const appManifest = dsRead.platform.getAppManifest(
        initRouterData.appDefinitionId,
      );
      const sortByKey =
        // @ts-expect-error
        appManifest?.pages?.sortInnerRoutes ||
        constants.DYNAMIC_PAGES.SORT_INNER_ROUTES.TITLE;
      if (sortByKey != constants.DYNAMIC_PAGES.SORT_INNER_ROUTES.NO_SORT) {
        // @ts-expect-error
        innerRoutes = _.sortBy(innerRoutes, [sortByKey]);
      }
      const newPrimaryPageId = dsRead.pages.getPrimaryPageId();
      const navigationWasDone = newPrimaryPageId !== initPrimaryPageId;

      const newRouterData =
        dsRead.routers.getRouterDataForPageIfExist(newPrimaryPageId);
      if (
        !isVariantOrReplacerPage &&
        (navigationWasDone || !_.isEqual(initRouterData, newRouterData))
      ) {
        dispatch(resetAllDynamicPageData());
      } else {
        dispatch(
          setFetchedRouteState(
            innerRoutes,
            newRouterData as RoutersDefinition,
            selectedRoute,
            dynamicPageIdOverride,
          ),
        );
      }
      return innerRoutes;
    });
  };

const setFetchedRouteState =
  (
    innerRoutes: SitemapEntry[],
    routerData: RoutersDefinition,
    selectedRoute: string,
    dynamicPageIdOverride: string | undefined,
  ) =>
  (
    dispatch: FunctionFixMe,
    getState: () => EditorState,
    { dsRead, dsActions }: { dsRead: DSRead; dsActions: DSAction },
  ) => {
    //category (collection) page case
    if (isCollectionPage(innerRoutes)) {
      dispatch(setInnerRoutes(innerRoutes));
      dispatch(setRouterData(routerData));
      dispatch(setSelectedInnerRoute(null));
      dispatch(navigateToRoute(innerRoutes[0].url, routerData.routerId));
      return;
    }

    const currentRouteInfo = dsRead.routers.getCurrentInnerRoute();
    const currInnerRoute = currentRouteInfo?.innerRoute;
    const selectedRouteIndex = selectedRoute
      ? findInnerRouteIndex(selectedRoute, innerRoutes)
      : null;
    const currentRoute = currInnerRoute
      ? findInnerRouteIndex(currInnerRoute, innerRoutes) || 0
      : 0;
    const index = _.isNumber(selectedRouteIndex)
      ? selectedRouteIndex
      : currentRoute;
    const newCurrentInnerRoute = innerRoutes[index]
      ? innerRoutes[index].url
      : null;

    dispatch(setInnerRoutes(innerRoutes));
    dispatch(setRouterData(routerData));
    if (
      currInnerRoute &&
      isEqualIgnoreCase(currInnerRoute, newCurrentInnerRoute)
    ) {
      dispatch(setSelectedInnerRoute(newCurrentInnerRoute));
    } else if (newCurrentInnerRoute) {
      dispatch(
        setSelectedInnerRouteAndNavigate(
          newCurrentInnerRoute,
          routerData.routerId,
          dynamicPageIdOverride,
        ),
      );
    } else if (!innerRoutes.length && currInnerRoute) {
      const pageId = dsRead.pages.getPrimaryPageId();
      dispatch(setSelectedInnerRoute(undefined));
      dsActions.pages.navigateTo(pageId);
    } else {
      dispatch(setSelectedInnerRoute(currInnerRoute));
    }
  };

const fetchInnerRoutesCounts =
  (pageIds: string[]) =>
  (
    dispatch: Dispatch,
    getState: () => EditorState,
    { dsRead }: { dsRead: DSRead },
  ) => {
    const routerIds: string[] = pageIds
      .map(dsRead.routers.getRouterDataForPageIfExist)
      .filter(Boolean)
      .map(({ routerId }) => routerId);

    const requests: Promise<{ pageName?: string; count?: number }>[] = _(
      routerIds,
    )
      .uniq()
      .map(
        (routerId) =>
          new Promise((resolve) => {
            dsRead.routers.getRouterInnerRoutesCount(
              routerId,
              undefined,
              resolve,
            );
          }),
      )
      .value();

    Promise.all(requests).then((responses) => {
      const countByPage = _(responses)
        .flatten()
        // @ts-expect-error
        .filter((r) => r?.pageName)
        .reduce((response, partialResponse) => {
          return {
            ...response,
            [partialResponse.pageName]: partialResponse.count,
          };
        }, {});

      dispatch(setInnerRoutesCounts({ innerRoutesCounts: countByPage }));
    });
  };

export {
  setCurrentRouteInfo,
  setInnerRoutes,
  resetInnerRoutes,
  setRouterData,
  setPreventFetch,
  resetRouterData,
  setSelectedInnerRoute,
  resetSelectedInnerRoute,
  setInnerRouteFetchProcess,
  resetInnerRouteFetchProcess,
  fetchInnerRoutes,
  resetAllDynamicPageData,
  updateDynamicPagesState,
  setSelectedInnerRouteAndNavigate,
  setInnerRoutesCounts,
  fetchInnerRoutesCounts,
  clearInnerRoutesCounts,
  preFetchAndCacheInnerRoutes,
};
